import {
  type LayoutArea,
  type TabHostApi,
  type TabHostOpenOptions,
  type TabHostTab,
  type ViewArea,
  type ViewCachePolicy,
} from '@hibro/shared';
import type { DockviewApi } from 'dockview-react';

import { resolveTabRenderer } from '../editor/editor-cache';
import { makeInstanceId, parseInstanceId } from '../editor/editor-tabs';
import { isTabbedArea, openAuxTab, TABBED_AREA } from './auxiliary-tabs';
import { isAreaVisibleNow, setAreaVisible } from './layout-builder';

type PendingOperation = (api: DockviewApi) => void;

/**
 * Stable module-facing wrapper around a dockview tabbed area. It deliberately
 * exposes no Dockview objects and queues intents until layout restore finishes.
 */
export class DockviewTabHost implements TabHostApi {
  private api: DockviewApi | undefined;
  private currentPhase: TabHostApi['phase'] = 'building';
  private currentRevision = 0;
  private readonly listeners = new Set<() => void>();
  private readonly pending: PendingOperation[] = [];
  private layoutDisposable: { dispose(): void } | undefined;

  constructor(
    readonly area: LayoutArea,
    private readonly moduleIdForView: (viewId: string) => string | undefined,
    /**
     * The view's declared keep-alive policy (§5.7 视图缓存), resolved by the
     * host from the contributed view set. Taken as a resolver rather than an
     * `open` option on purpose: caching is a manifest property of the view, and
     * `TabHostApi` is a module-facing surface — a module must not be able to
     * re-declare (or contradict) it per call.
     */
    private readonly cachePolicyForView: (viewId: string) => ViewCachePolicy,
    /**
     * Declared home of a view. Editor documents parked on the auxiliary strip
     * are not that area's module projection and must survive the next open.
     */
    private readonly areaForView: (
      viewId: string,
    ) => ViewArea | undefined = () => undefined,
  ) {}

  get phase(): TabHostApi['phase'] {
    return this.currentPhase;
  }

  get revision(): number {
    return this.currentRevision;
  }

  get visible(): boolean {
    return this.api ? isAreaVisibleNow(this.api, this.area) : false;
  }

  attach(api: DockviewApi): void {
    this.layoutDisposable?.dispose();
    this.api = api;
    this.beginRestore();
    this.layoutDisposable = api.onDidLayoutChange(() => this.emit());
    this.endRestore();
  }

  /** Platform-internal lifecycle bracket around an in-place layout rebuild. */
  beginRestore(): void {
    if (!this.api) {
      return;
    }
    this.currentPhase = 'restoring';
    this.emit();
  }

  /** Complete a rebuild, advance its revision, then flush queued intents. */
  endRestore(): void {
    const api = this.api;
    if (!api) {
      return;
    }
    this.currentRevision += 1;
    this.currentPhase = 'ready';
    const queued = this.pending.splice(0);
    for (const operation of queued) {
      operation(api);
    }
    this.emit();
  }

  detach(): void {
    this.layoutDisposable?.dispose();
    this.layoutDisposable = undefined;
    this.api = undefined;
    this.currentPhase = 'building';
    this.emit();
  }

  getTabs(): readonly TabHostTab[] {
    const api = this.api;
    const anchor = api?.getPanel(this.area);
    if (!api || !anchor) {
      return [];
    }
    const activeId = anchor.group.activePanel?.id;
    return anchor.group.panels
      .filter((panel) => panel.id !== this.area)
      .map((panel) => {
        const parsed = parseInstanceId(panel.id);
        const params = panel.params as
          | { moduleId?: string; title?: string }
          | undefined;
        return {
          id: panel.id,
          area: this.area,
          viewId: parsed.viewId,
          moduleId:
            params?.moduleId ??
            this.moduleIdForView(parsed.viewId) ??
            parsed.viewId,
          instanceKey: parsed.instanceKey,
          title: panel.api.title ?? params?.title ?? parsed.viewId,
          active: panel.id === activeId,
        };
      });
  }

  isOpen(viewId: string, instanceKey?: string): boolean {
    return (
      this.api?.getPanel(makeInstanceId(viewId, instanceKey)) !== undefined
    );
  }

  open(options: TabHostOpenOptions): void {
    this.run((api) => {
      if (!isTabbedArea(this.area)) {
        return;
      }
      // Single-occupant eviction is an **auxiliaryBar** policy, not a property of
      // tabbed areas: that panel projects one module at a time. Only another
      // auxiliary view is that projection. An editor document the user dragged
      // onto the strip stays; closing it here disposes the instance (见 20 二期).
      if (this.area === TABBED_AREA) {
        const anchor = api.getPanel(this.area);
        for (const panel of anchor?.group.panels ?? []) {
          if (panel.id === this.area) {
            continue;
          }
          const parsed = parseInstanceId(panel.id);
          if (this.areaForView(parsed.viewId) === 'editor') {
            continue;
          }
          const params = panel.params as { moduleId?: string } | undefined;
          const panelModuleId =
            params?.moduleId ?? this.moduleIdForView(parsed.viewId);
          if (panelModuleId && panelModuleId !== options.moduleId) {
            // Replacing its projection removes layout tabs only; module-owned
            // data is untouched.
            api.removePanel(panel);
          }
        }
      }
      openAuxTab(
        api,
        {
          ...options,
          renderer: resolveTabRenderer(this.cachePolicyForView(options.viewId)),
        },
        this.area,
      );
    });
  }

  focus(viewId: string, instanceKey?: string): void {
    this.run((api) => {
      api.getPanel(makeInstanceId(viewId, instanceKey))?.api.setActive();
    });
  }

  /**
   * Reveal the area's structural anchor — the panel that hosts the singleton
   * EditorSlot. A rail click is `activeEditorView` and must land here; if an
   * instance tab is still the group's active panel, the click only updates
   * React state behind it and looks like "侧边 tab 点不动".
   */
  showAnchor(): void {
    this.run((api) => {
      api.getPanel(this.area)?.api.setActive();
    });
  }

  close(viewId: string, instanceKey?: string): void {
    this.run((api) => {
      const panel = api.getPanel(makeInstanceId(viewId, instanceKey));
      if (panel) {
        api.removePanel(panel);
      }
    });
  }

  setTitle(
    viewId: string,
    instanceKey: string | undefined,
    title: string,
  ): void {
    this.run((api) => {
      api.getPanel(makeInstanceId(viewId, instanceKey))?.api.setTitle(title);
    });
  }

  setVisible(visible: boolean): void {
    this.run((api) => setAreaVisible(api, this.area, visible));
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private run(operation: PendingOperation): void {
    if (this.api && this.currentPhase === 'ready') {
      operation(this.api);
      this.emit();
      return;
    }
    this.pending.push(operation);
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
